import React, { useState, useRef, useEffect } from 'react'
import { Loader } from '@googlemaps/js-api-loader'
import { useTranslation } from 'react-i18next'
import toLower from 'lodash/toLower'

// initialCenter: optional { lat, lng, zoom?, bounds? } used when no location is picked yet.
const LocationPicker = ({ onLocationSelect, initialLat, initialLon, onClose, initialCenter, countryCode }) => {
  const { t } = useTranslation()
  const [map, setMap] = useState(null)
  const mapRef = useRef(null)
  const searchRef = useRef(null)
  const markerRef = useRef(null)

  const [address, setAddress] = useState('')
  const [selectedLocation, setSelectedLocation] = useState(null)

  const googleMapKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  useEffect(() => {
    const loader = new Loader({
      apiKey: googleMapKey,
      version: 'weekly',
      libraries: ['places'],
    })

    loader.load().then(() => {
      const mapOptions = {
        center: {
          lat: initialLat || initialCenter?.lat || 23.588,
          lng: initialLon || initialCenter?.lng || 58.3829,
        },
        zoom: (!initialLat && initialCenter?.zoom) || 10,
      }

      const mapInstance = new window.google.maps.Map(mapRef.current, mapOptions)
      setMap(mapInstance)

      if (initialLat && initialLon) {
        const initialPosition = { lat: initialLat, lng: initialLon }
        const initialMarker = new window.google.maps.Marker({
          position: initialPosition,
          map: mapInstance,
          draggable: true,
        })
        markerRef.current = initialMarker

        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode({ location: initialPosition }, (results, status) => {
          const resolvedAddress = status === 'OK' && results[0] ? results[0].formatted_address : ''
          setAddress(resolvedAddress)
          setSelectedLocation({
            lat: initialLat,
            lon: initialLon,
            address: resolvedAddress,
          })
        })
      }

      if (!(initialLat && initialLon) && initialCenter?.bounds) mapInstance.fitBounds(initialCenter.bounds)

      const autocomplete = new window.google.maps.places.Autocomplete(searchRef.current)
      autocomplete.bindTo('bounds', mapInstance)
      if (countryCode) autocomplete.setComponentRestrictions({ country: toLower(countryCode) })

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        if (!place.geometry || !place.geometry.location) return

        mapInstance.setCenter(place.geometry.location)
        mapInstance.setZoom(14)

        if (markerRef.current) {
          markerRef.current.setMap(null)
        }

        const newMarker = new window.google.maps.Marker({
          position: place.geometry.location,
          map: mapInstance,
          draggable: true,
        })
        markerRef.current = newMarker

        const newAddress = place.formatted_address || ''
        setAddress(newAddress)

        setSelectedLocation({
          lat: place.geometry.location.lat(),
          lon: place.geometry.location.lng(),
          address: newAddress,
        })
      })

      mapInstance.addListener('click', event => {
        if (markerRef.current) {
          markerRef.current.setMap(null)
        }

        const newMarker = new window.google.maps.Marker({
          position: event.latLng,
          map: mapInstance,
          draggable: true,
        })
        markerRef.current = newMarker

        const lat = event.latLng.lat()
        const lng = event.latLng.lng()

        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode({ location: event.latLng }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const newAddress = results[0].formatted_address || ''
            setAddress(newAddress)

            setSelectedLocation({
              lat,
              lon: lng,
              address: newAddress,
            })
          } else {
            setSelectedLocation({
              lat,
              lon: lng,
              address: '',
            })
          }
        })
      })
    })
  }, [])

  const handleConfirmLocation = () => {
    if (selectedLocation) {
      onLocationSelect(selectedLocation)
    }
  }

  return (
    <div className="relative w-full h-96">
      <input
        type="text"
        ref={searchRef}
        className="absolute top-2 left-1/2 transform -translate-x-1/2 w-11/12 max-w-md p-2 border rounded-lg bg-white shadow-md z-10 mt-10"
        placeholder={t('common.placeholders.searchLocation')}
      />
      <div ref={mapRef} className="w-full h-full rounded-lg mt-10" />

      <div className="mt-4 w-full flex justify-between">
        <button type="button" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none" onClick={onClose}>
          Close
        </button>

        <button type="button" className="px-4 py-2 bg-dark-600 text-white rounded-md hover:bg-dark-700 focus:outline-none" onClick={handleConfirmLocation}>
          Confirm Location
        </button>
      </div>
    </div>
  )
}

export default LocationPicker
